/**
 * Numeric scales, and the step the user did not have to think about.
 *
 * A user supplies a minimum and a maximum. Asking them for a step as well is a
 * question most people cannot answer well, and a naive `range / 10` produces
 * steps like 0.9 or 3.3 — worse than offering nothing. So it is derived to a
 * round number, and remains overridable.
 */

import type { NumericKind } from './track.js'

/** Steps are always one of these times a power of ten. */
const NICE_MULTIPLES = [1, 2, 5, 10] as const

/** Aim for roughly this many increments across the range. */
const TARGET_INCREMENTS = 15

/**
 * Kills floating-point artefacts without rounding away real precision.
 *
 * `Math.pow(10, n)` and the multiplication below can produce values like
 * 0.30000000000000004, which then leak into every tick label and stored value.
 */
function toPrecision(value: number, significantDigits = 12): number {
  return Number.parseFloat(value.toPrecision(significantDigits))
}

/**
 * A round step giving roughly 10–20 increments across the range.
 *
 * ```
 * range      = max - min
 * raw        = range / 15
 * magnitude  = 10 ^ floor(log10(raw))
 * normalised = raw / magnitude
 * snapped    = first of 1, 2, 5, 10 that is >= normalised
 * step       = snapped * magnitude
 * ```
 *
 * So 1–10 gives 1, 0–1 gives 0.1, 0–100 gives 10 and 0–5 gives 0.5.
 */
export function deriveStep(min: number, max: number): number {
  const range = max - min
  if (!Number.isFinite(range) || range <= 0) {
    return 1
  }

  const raw = range / TARGET_INCREMENTS
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const normalised = raw / magnitude
  const snapped = NICE_MULTIPLES.find((candidate) => candidate >= normalised) ?? 10

  return toPrecision(snapped * magnitude)
}

/** The decimal places a step implies: 0.5 needs one, 1 needs none. */
export function deriveDecimals(step: number): number {
  if (!Number.isFinite(step) || step <= 0) {
    return 0
  }
  const text = toPrecision(step).toString()
  if (text.includes('e')) {
    // Exponential form, e.g. 1e-7. The exponent is the decimal count.
    const exponent = Number(text.slice(text.indexOf('e') + 1))
    return exponent < 0 ? Math.abs(exponent) : 0
  }
  const point = text.indexOf('.')
  return point === -1 ? 0 : text.length - point - 1
}

/**
 * A scale as the editor holds it, remembering which parts the user chose.
 *
 * The distinction matters when the bounds change: a step the user never touched
 * should follow the new range, while one they set deliberately should not be
 * quietly overwritten.
 */
export interface ScaleConfig {
  readonly min: number
  readonly max: number
  readonly step: number
  readonly decimals: number
  readonly explicit: {
    readonly step: boolean
    readonly decimals: boolean
  }
}

export interface ScaleOverrides {
  readonly step?: number
  readonly decimals?: number
}

export function makeScale(
  min: number,
  max: number,
  overrides: ScaleOverrides = {},
): ScaleConfig {
  const step = overrides.step ?? deriveStep(min, max)
  const decimals = overrides.decimals ?? deriveDecimals(step)
  return {
    min,
    max,
    step,
    decimals,
    explicit: {
      step: overrides.step !== undefined,
      decimals: overrides.decimals !== undefined,
    },
  }
}

/** Changes the bounds, re-deriving whatever the user did not set themselves. */
export function withBounds(scale: ScaleConfig, min: number, max: number): ScaleConfig {
  const step = scale.explicit.step ? scale.step : deriveStep(min, max)
  const decimals = scale.explicit.decimals ? scale.decimals : deriveDecimals(step)
  return { ...scale, min, max, step, decimals }
}

/** Sets the step explicitly, so later bound changes leave it alone. */
export function withStep(scale: ScaleConfig, step: number): ScaleConfig {
  return {
    ...scale,
    step,
    decimals: scale.explicit.decimals ? scale.decimals : deriveDecimals(step),
    explicit: { ...scale.explicit, step: true },
  }
}

/** Returns to a derived step, following the bounds again. */
export function withDerivedStep(scale: ScaleConfig): ScaleConfig {
  const step = deriveStep(scale.min, scale.max)
  return {
    ...scale,
    step,
    decimals: scale.explicit.decimals ? scale.decimals : deriveDecimals(step),
    explicit: { ...scale.explicit, step: false },
  }
}

export function toNumericKind(scale: ScaleConfig): NumericKind {
  return {
    kind: 'numeric',
    min: scale.min,
    max: scale.max,
    step: scale.step,
    decimals: scale.decimals,
  }
}

/**
 * Recovers an editable scale from a stored track.
 *
 * Whether the step was derived or chosen is not stored, so it is inferred:
 * a step matching what the range would derive is treated as derived. The
 * heuristic errs harmlessly — a user who explicitly picked the value the range
 * would have produced anyway gets the same number either way, and it means
 * widening a range updates the step for everyone who never overrode it.
 */
export function fromNumericKind(kind: NumericKind): ScaleConfig {
  const derivedStep = deriveStep(kind.min, kind.max)
  const stepWasDerived = kind.step === derivedStep
  const decimalsWereDerived = kind.decimals === deriveDecimals(kind.step)

  return {
    min: kind.min,
    max: kind.max,
    step: kind.step,
    decimals: kind.decimals,
    explicit: { step: !stepWasDerived, decimals: !decimalsWereDerived },
  }
}

/** The nearest valid value on the scale, clamped to the bounds. */
export function snapToStep(value: number, scale: ScaleConfig): number {
  if (!Number.isFinite(value)) {
    return scale.min
  }
  const clamped = Math.min(Math.max(value, scale.min), scale.max)
  const steps = Math.round((clamped - scale.min) / scale.step)
  return toPrecision(
    Math.min(scale.min + steps * scale.step, scale.max),
    // Rounding to the scale's own precision is what stops a drag producing
    // 0.30000000000000004 and storing it forever.
    Math.max(scale.decimals, 1) + 10,
  )
}

/**
 * Every valid value on the scale, in order.
 *
 * Computed as `min + i * step` rather than by repeated addition: accumulating
 * a step of 0.1 visibly drifts by the tenth tick.
 */
export function tickValues(scale: ScaleConfig): number[] {
  if (scale.step <= 0 || scale.max <= scale.min) {
    return [scale.min]
  }
  const count = Math.floor((scale.max - scale.min) / scale.step)
  const ticks: number[] = []
  for (let index = 0; index <= count; index += 1) {
    ticks.push(toPrecision(scale.min + index * scale.step, scale.decimals + 10))
  }
  // The maximum is a valid value even when the step does not divide the range.
  const last = ticks[ticks.length - 1]
  if (last !== undefined && last < scale.max) {
    ticks.push(scale.max)
  }
  return ticks
}
